import { ChildModel, ModelBase } from 'store/model-base';
import { ChargeType, PaymentState, SERVICE_PROMO_ACCOUNT, StatementRouteParam, StatementSummaryId } from 'my-account/constants';
import { PaymentInfo } from 'store/credit-card/models';
import { StatementLineItem } from './line/item';
import { LineItemCharge } from './line/charge';
import { StatementCredit } from './credit';
import { StatementOnetimeCharges } from './onetime-charges';
import { StatementPlanChange } from './plan-change';
import { StatementDataDetails } from './data/detail';
import { Util } from 'services/util';
import { OrderTaxFee } from 'store/order/models';
import { CmsBillingStatement } from 'store/cms/models';

export class Statement extends ModelBase {
    public static storeName: string = 'statements';
    public static idAttribute: string = 'id.key';

    public id: Id;
    public cycleStartDate: string;
    public cycleEndDate: string;
    public cycleTotal: number;
    public paymentState: PaymentState;
    public isLegacy: boolean;
    public autoPayDate: string;
    public paymentInfo: PaymentInfo;
    public planChanges: StatementPlanChange[];
    public hasSharedData: boolean;
    public byTheGigDataCost: number;
    public data: StatementDataDetails;
    public statementLineItems: StatementLineItem[];
    public credits: StatementCredit[];
    public serviceCredits: StatementCredit[];
    public creditReversals: StatementCredit[];
    public taxAndFees: OrderTaxFee[];
    public taxTotal: number;
    public serviceCreditTotal: number;
    public reverseCreditTotal: number;
    public promoCredits: StatementCredit[];
    public promoCharges: LineItemCharge[];
    public lineItemcharges: LineItemCharge[];
    public accountLevelFees: LineItemCharge[];
    public oneTimeCharges: StatementOnetimeCharges;
    public oneTimeTotal: number;
    public oneTimeChargesList: OneTimeCharge[];

    public localeAutoPayDate: string;
    public createdDate: string;
    public hasCredits: boolean;
    public isEstimated: boolean;
    public isPaid: boolean;
    public isPastDue: boolean;
    public isNextPayment: boolean;
    public dateRange: string;

    public static create<T extends ModelBase>(response: ApiResponse, id?: string): T {
        if (!response.id) {
            response.id = {
                value: '',
                key: id
            };
        } else {
            response.id.key = id;
        }

        response.serviceCredits = response.credits.filter((data: StatementCredit) => data.creditType !== SERVICE_PROMO_ACCOUNT);
        
        response.serviceCreditTotal = response.serviceCredits && response.serviceCredits.reduce((total: number, credit: StatementCredit) => total + credit.creditAmount, 0);
        response.reverseCreditTotal = response.creditReversals && response.creditReversals.reduce((total: number, credit: StatementCredit) => total + credit.creditAmount, 0);

        response.hasCredits = response.serviceCredits && response.serviceCredits.length > 0;
        response.promoCredits = response.credits.filter((data: StatementCredit) => data.creditType === SERVICE_PROMO_ACCOUNT);
        response.lineItemcharges = response.statementLineItems.map((lineItem: StatementLineItem) => lineItem.charges);
        response.promoCharges = response.lineItemcharges.flat().filter((charge: LineItemCharge) => charge.chargeCategory?.toUpperCase() === ChargeType.SERVICE_PROMO_LINE);

        response.isEstimated = response.paymentState === PaymentState.ESTIMATED;
        response.isPaid = response.paymentState === PaymentState.PAID;
        response.isPastDue = response.paymentState === PaymentState.PAST_DUE;
        response.isNextPayment = response.paymentState === PaymentState.PENDING;

        response.localeAutoPayDate = Util.dateFormat(response.autoPayDate, 'MMM dd');
        response.dateRange = Statement.getDateRange(response);

        response.oneTimeChargesList = response?.oneTimeCharges?.charges;
        response.oneTimeTotal = response?.oneTimeCharges?.oneTimeTotal;

        if (response.isPaid) {
            response.createdDate = response.paymentInfo && Util.dateFormat(response.paymentInfo.createdDate, 'MMM dd yyyy');
        }

        return super.create<T>(response);
    }

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'paymentInfo',
            model: PaymentInfo
        },
        {
            attrName: 'data',
            model: StatementDataDetails
        }];
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'statementLineItems',
            model: StatementLineItem
        }, {
            attrName: 'credits',
            model: StatementCredit
        }, {
            attrName: 'creditReversals',
            model: StatementCredit
        }, {
            attrName: 'planChanges',
            model: StatementPlanChange
        }, {
            attrName: 'taxAndFees',
            model: OrderTaxFee
        }];
    }

    public static getDateRange(data: ApiResponse): string {
        const startDate: string = Util.dateFormat(data.cycleStartDate, 'MMM dd');
        const endDate: string = Util.dateFormat(data.cycleEndDate, 'MMM dd');

        return `${startDate} - ${endDate}`;
    }

    public buildSummaryCms(cmsData: CmsBillingStatement, paymentState: StatementRouteParam): CmsStatementSummary {
        let summaryContentId: string = '';

        if (this.isEstimated) {
            summaryContentId = StatementSummaryId.ESTIMATED;
        } else if (this.isPaid) {
            summaryContentId = StatementSummaryId.PAID;
        } else if (this.isNextPayment && paymentState === StatementRouteParam.PENDING) {
            summaryContentId = StatementSummaryId.NEXT;
        } else if (this.isNextPayment && paymentState === StatementRouteParam.PENDING_WITH_CREDIT) {
            summaryContentId = StatementSummaryId.NEXT_WITH_CREDIT;
        } else if (this.isPastDue) {
            summaryContentId = StatementSummaryId.PAST_DUE;
        }

        return cmsData.summaryContents.find((each: CmsStatementSummary) => each.id === summaryContentId);
    }

    public getStatementHeading(content: CmsStatementSummary): string {
        return content.pageTitle.replace('[DATE_RANGE]', this.dateRange);
    }
}
